import React, { useState } from 'react';
import { useObservable } from 'react-use-observable';

import { Button, Spin, Tooltip } from 'antd';

import { EChargeType } from 'interfaces/IEditProduct';
import { BuyerRefundRequestModal } from 'v2/components/Shared/RefundReason';
import { redirectUserToNewRefundService } from 'v2/helpers/redirectUserToNewRefundService';
import { EPaymentMethod, type IRefundRequest, type ShoppingDelivery } from 'v2/interfaces/shopping';
import newRefundService from 'v2/services/new-refund-service';
import type { NewRefundServicePermission } from 'v2/services/new-refund-service';
import { refundRequestFactory } from 'v2/services/shopping/refundRequest';

const RefundActions = ({ delivery }: { delivery: ShoppingDelivery }) => {
  const [user] = useObservable<NewRefundServicePermission>(
    () => newRefundService.getPermissionToNewRefundServiceByInvoiceId(delivery.invoice_id.toString()),
    []
  );
  const [open, setOpen] = useState<boolean>(false);
  const [loading, setLoading] = useState<boolean>(true);
  const [refundRequest] = useObservable<IRefundRequest>(
    () => refundRequestFactory.buyerFindRefundByInvoiceId(delivery.invoice_id),
    []
  );

  React.useEffect(() => {
    if (refundRequest !== null) {
      setLoading(false);
    }
  }, [refundRequest]);

  const openRefundRequestModal = () => {
    setOpen(true);
  };

  const closeRefundRequestModal = () => {
    setOpen(false);
  };

  const onRefundRequestMade = () => {
    setOpen(false);
  };

  const isFreeProduct = delivery.charge_type === EChargeType.FREE;
  const canShowRefundRequestButton = refundRequest && !refundRequest.exist && delivery?.product.valid_to_refund_request;

  const hasPermissionToUseNewRefundService = user && user.success && user.data.producer_can_access;

  if (loading) {
    return (
      <div className='flex w-full justify-center p-2.5'>
        <Spin />
      </div>
    );
  }

  if (refundRequest.exist && hasPermissionToUseNewRefundService) {
    return (
      <div className='w-full'>
        <Button
          type='primary'
          onClick={() => redirectUserToNewRefundService(delivery.invoice_id.toString(), 'tracking')}
          block
        >
          Acompanhar Reembolso
        </Button>
      </div>
    );
  }

  if (isFreeProduct) {
    return null;
  }

  if (delivery.product.type === 'Ticket' && !canShowRefundRequestButton && !hasPermissionToUseNewRefundService) {
    return (
      <div className='w-full'>
        <Tooltip title='Poxa! O prazo para solicitar reembolso é de até 7 dias após a compra ou no máximo 48 horas antes do evento. Para solicitar o reembolso, por gentileza, entre em contato diretamente com o produtor do evento.'>
          <span>
            <Button type='primary' danger className='opacity-70' disabled block>
              Solicitar Reembolso
            </Button>
          </span>
        </Tooltip>
      </div>
    );
  }

  if (
    delivery.payment_method.id === EPaymentMethod.BOLETO_PARCELADO &&
    !canShowRefundRequestButton &&
    !hasPermissionToUseNewRefundService
  ) {
    return (
      <div className='w-full'>
        <Tooltip title='Não é possível reembolsar faturas de Boleto Parcelado TMB'>
          <span>
            <Button type='primary' danger className='opacity-70' disabled block>
              Solicitar Reembolso
            </Button>
          </span>
        </Tooltip>
      </div>
    );
  }

  if (!canShowRefundRequestButton) {
    return null;
  }

  return (
    <>
      <div className='w-full'>
        <Button
          type='primary'
          danger
          onClick={
            hasPermissionToUseNewRefundService
              ? () => redirectUserToNewRefundService(delivery.invoice_id.toString(), 'request')
              : openRefundRequestModal
          }
          block
        >
          Solicitar Reembolso
        </Button>
      </div>
      <BuyerRefundRequestModal
        open={open}
        invoiceId={delivery.invoice_id}
        onClose={closeRefundRequestModal}
        onFinish={onRefundRequestMade}
      />
    </>
  );
};

export default RefundActions;
